import { useState, useRef } from "react";
import { DateTime } from "luxon";
import { getLongDateFormat } from "../../utils/luxonHelpers";
import i18n from "../../utils/i18n";
import ShiftsBatchFormDateInput from "./ShiftsBatchFormDateInput";
import RadioButton from "../RadioButton";

const ShiftsBatchForm = ({
  batch_shifts,
  onSubmitHandler,
  addPicker,
  removePicker,
  handleChange,
}) => {
  const [validationError, setValidationError] = useState(null);
  const [dayEvening, setDayEvening] = useState(null);
  const [beforeAfter, setBeforeAfter] = useState(null);

  const attendanceLimitRef = useRef(null);
  const meetingPointRef = useRef(null);

  const validateShiftAttributes = () => {
    const attendanceLimit = attendanceLimitRef.current?.value.trim();
    const meetingPoint = meetingPointRef.current?.value.trim();

    return attendanceLimit && meetingPoint;
  };

  const validateStartEndDate = (picker) => {
    let validationErrorMsg = "";

    const { start_date, start_time, end_date, end_time } = picker;

    if (!start_date || !start_time || !end_date || !end_time) {
      validationErrorMsg = i18n.t(
        "activemodel.errors.models.shift.invalid_date_and_time"
      );
    }

    const longDateFormat = getLongDateFormat();
    const start = DateTime.fromFormat(
      start_date + " " + start_time,
      longDateFormat + " HH:mm"
    );
    const end = DateTime.fromFormat(
      end_date + " " + end_time,
      longDateFormat + " HH:mm"
    );

    if (!start.isValid || !end.isValid) {
      validationErrorMsg = i18n.t(
        "activemodel.errors.models.shift.invalid_date_and_time"
      );
    }

    if (end < start) {
      validationErrorMsg = i18n.t(
        "activemodel.errors.models.shift.end_time_before_start_time"
      );
    }

    setValidationError(validationErrorMsg);

    if (validationErrorMsg !== "") return null;

    return { start: start, end: end };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateShiftAttributes()) return;

    const batchShiftsToSubmit = [];
    let result = null;

    for (let i = 0; i < batch_shifts.length; i++) {
      const shift = batch_shifts[i];
      result = validateStartEndDate(shift);

      if (result) {
        shift.start_time = result.start;
        shift.end_time = result.end;
        shift.attendance_limit = attendanceLimitRef.current.value.trim();
        shift.meeting_point = meetingPointRef.current.value.trim();
        shift.day_evening = dayEvening;
        shift.before_after = beforeAfter;
        batchShiftsToSubmit.push(shift);
      } else {
        break;
      }
    }

    if (result) {
      onSubmitHandler(batchShiftsToSubmit);
    }
  };

  const handleAddPicker = () => {
    addPicker();
  };

  const handleDayEveningChange = (value) => {
    setDayEvening(value === "neither" ? null : value);
  };

  const handleBeforeAfterChange = (value) => {
    setBeforeAfter(value === "neither" ? null : value);
  };

  const dayEveningInput = (value, checked) => {
    return (
      <RadioButton
        label={i18n.t("schedule.shifts.form.day_evening_labels." + value)}
        value={value}
        checked={checked}
        onChange={handleDayEveningChange}
      />
    );
  };

  const beforeAfterInput = (value, checked) => {
    return (
      <RadioButton
        label={i18n.t("schedule.shifts.form.before_after_labels." + value)}
        value={value}
        checked={checked}
        onChange={handleBeforeAfterChange}
      />
    );
  };

  const renderErrorBlock = () => {
    return (
      <div>
        {validationError && (
          <div className="alert alert-danger">{validationError}</div>
        )}
      </div>
    );
  };

  const renderDateTimePickers = () => {
    const hasRemoveButton = batch_shifts.length > 1;

    return (
      <div>
        {batch_shifts.map((picker, index) => (
          <ShiftsBatchFormDateInput
            key={index}
            index={index}
            has_remove_button={hasRemoveButton}
            removePicker={removePicker}
            handleChange={handleChange}
            picker={picker}
            onChange={handleChange}
            from_label={i18n.t("schedule.shifts.form.from")}
            to_label={i18n.t("schedule.shifts.form.to")}
            at_label={i18n.t("schedule.shifts.form.at")}
            placeholder_date={i18n.t("schedule.shifts.form.date_placeholder")}
            placeholder_time={i18n.t("schedule.shifts.form.time_placeholder")}
          />
        ))}
      </div>
    );
  };

  return (
    <form className="form batch-form" onSubmit={handleSubmit}>
      <div className="modal-body">
        {renderErrorBlock()}
        {renderDateTimePickers()}
        <div className="form-group">
          <button
            type="button"
            className="pull-right text-info btn-add-picker"
            onClick={handleAddPicker}
          >
            {" "}
            + {i18n.t("schedule.shifts.add_shift")}
          </button>
          <div className="clearfix"></div>
        </div>
        <div className="form-group radio_buttons optional">
          <label className="radio_buttons optional control-label">
            {i18n.t("schedule.shifts.form.day_evening")}
          </label>
          {dayEveningInput("neither", dayEvening === null)}
          {dayEveningInput("day", dayEvening === "day")}
          {dayEveningInput("evening", dayEvening === "evening")}
          <span className="help-block">
            {i18n.t("schedule.shifts.form.day_evening_help", {
              day: 1,
              evening: 1,
            })}
          </span>
        </div>
        <div className="form-group radio_buttons optional">
          <label className="radio_buttons optional control-label">
            {i18n.t("schedule.shifts.form.before_after")}
          </label>
          {beforeAfterInput("neither", beforeAfter === null)}
          {beforeAfterInput("before", beforeAfter === "before")}
          {beforeAfterInput("after", beforeAfter === "after")}
          <span className="help-block">
            {i18n.t("schedule.shifts.form.before_after_help", {
              before: 1,
              after: 1,
            })}
          </span>
        </div>
        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.member_limit")}</label>
          <input
            type="integer"
            name="attendance_limit"
            required
            className="form-control"
            placeholder={i18n.t(
              "schedule.shifts.form.member_limit_placeholder"
            )}
            ref={attendanceLimitRef}
          />
        </div>
        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.meeting_point")}</label>
          <input
            type="text"
            name="meeting_point"
            required
            className="form-control"
            placeholder={i18n.t(
              "schedule.shifts.form.meeting_point_placeholder"
            )}
            ref={meetingPointRef}
          />
        </div>
      </div>
      <div className="modal-footer">
        <input
          type="submit"
          className="btn btn-primary"
          value={i18n.t("schedule.shifts.form.create")}
        />
      </div>
    </form>
  );
};

export default ShiftsBatchForm;
